/**
 * What a visit knows about itself, kept until a form is submitted.
 *
 * The campaign that brought someone here is on the URL when they land and gone
 * by the time they fill anything in, and the browser they used is never written
 * down anywhere we can read later. Both are remembered at the start of the
 * visit and sent with the submission, so an inquiry can be traced back to the
 * outreach that produced it.
 *
 * Storage is per tab on purpose. A campaign belongs to the visit it arrived
 * with, not to the browser forever: remembering it past the end of the session
 * would credit a later, unrelated visit to an old email.
 */

const STORAGE_KEY = "pollinator_attribution";

/** The UTM parameters worth keeping. Outreach links here use source, medium and term. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
] as const;

/** Which call to action led to each form, by the form's slug. */
const CTA_STORAGE_KEY = "pollinator_cta";

/** The anchor on the address this visit arrived at, if it arrived at one. */
const LANDING_ANCHOR_KEY = "pollinator_landing_anchor";

export type Attribution = {
  analytics_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
};

function read(): Attribution {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Attribution) : {};
  } catch {
    // Private windows and blocked storage both throw here. Attribution is
    // worth having and never worth breaking a form over.
    return {};
  }
}

/**
 * Record the campaign parameters on the URL, if this visit arrived with any.
 *
 * Called once when the app starts rather than when a form mounts, because by
 * the time someone reaches the form they have usually navigated and the
 * parameters are no longer on the address.
 *
 * A later page without parameters does not erase what the visit arrived with:
 * the first link that carried them is the one that did the work.
 */
export function rememberAttribution(search = window.location.search): void {
  const params = new URLSearchParams(search);
  const found: Attribution = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) found[key] = value;
  });

  if (Object.keys(found).length === 0) return;

  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...read(), ...found })
    );
  } catch {
    // Nothing to do. The submission still goes through without it.
  }
}

/**
 * Everything to send alongside a submission.
 *
 * The analytics id is read at submission time rather than stored, because
 * PostHog may not have loaded when the visit began and it is the identity the
 * events were actually recorded under.
 */
export function getAttribution(): Attribution {
  const stored = read();

  let analyticsId: string | undefined;
  try {
    analyticsId = window.posthog?.get_distinct_id?.();
  } catch {
    analyticsId = undefined;
  }

  return analyticsId ? { ...stored, analytics_id: analyticsId } : stored;
}

/**
 * Note that a call to action leading to a form was clicked.
 *
 * A form is usually reached from one of several buttons, and by the time it is
 * on screen nothing says which. The click is remembered against the form it
 * leads to, for the length of the visit, so that what happens on the form can
 * be credited to the button that brought somebody there. A later click for the
 * same form replaces an earlier one: the last button pressed is the one that
 * worked.
 */
export function rememberCta(ctaName: string, formSlug: string): void {
  try {
    const stored = window.sessionStorage.getItem(CTA_STORAGE_KEY);
    const remembered = stored ? (JSON.parse(stored) as Record<string, string>) : {};
    window.sessionStorage.setItem(
      CTA_STORAGE_KEY,
      JSON.stringify({ ...remembered, [formSlug]: ctaName })
    );
  } catch {
    // Nothing to do. The form works the same without it.
  }
}

/**
 * Record the anchor this visit arrived at, such as `#newsletter`.
 *
 * Called once when the app starts, for the same reason the campaign is: the
 * address a visit began on is the only evidence of a link somebody followed
 * from outside, and it is gone as soon as they navigate. Only the first
 * address of a visit counts, so a later one never replaces it.
 */
export function rememberLanding(hash = window.location.hash): void {
  try {
    if (window.sessionStorage.getItem(LANDING_ANCHOR_KEY) !== null) return;
    window.sessionStorage.setItem(LANDING_ANCHOR_KEY, hash.replace(/^#/, ""));
  } catch {
    // Nothing to do. Forms work the same without it.
  }
}

/**
 * Where somebody on a form came from: how they arrived at it, the call to
 * action that led them to it if this visit clicked one, and the campaign the
 * visit arrived with.
 *
 * `arrived_by` is always present, so that each way of arriving can be counted
 * rather than inferred from a gap. Somebody who clicked one of the site's
 * links or buttons for this form arrived by "cta". For a form with a page of
 * its own, anybody else followed a link straight to it, from an email or a
 * message or another site, and arrived "direct".
 *
 * A form set into a longer page is different, because nobody has to choose to
 * reach it. Pass the `anchor` it sits at: somebody whose visit began at that
 * anchor followed a link to the form and arrived "direct", and somebody who
 * neither clicked nor landed there came down the page to it and "scrolled".
 */
export function getFormSource(
  formSlug: string,
  anchor?: string
): {
  arrived_by: "cta" | "direct" | "scrolled";
  cta_name?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
} {
  let ctaName: string | undefined;
  try {
    const stored = window.sessionStorage.getItem(CTA_STORAGE_KEY);
    ctaName = stored
      ? (JSON.parse(stored) as Record<string, string>)[formSlug]
      : undefined;
  } catch {
    ctaName = undefined;
  }

  let landedAtAnchor = false;
  if (anchor) {
    try {
      landedAtAnchor = window.sessionStorage.getItem(LANDING_ANCHOR_KEY) === anchor;
    } catch {
      landedAtAnchor = false;
    }
  }

  return {
    ...read(),
    ...(ctaName ? { cta_name: ctaName } : {}),
    arrived_by: ctaName ? "cta" : anchor && !landedAtAnchor ? "scrolled" : "direct",
  };
}
